/**
 * Labeler guidance content for the labeling tool.
 *
 * Pure data so the "How to label" guide can't silently drift from the
 * labelable fields — `labelGuide.test.ts` asserts FIELD_GUIDE covers exactly
 * the schema fields the form edits. The wording mirrors the pinned
 * `server_py.extraction.label_schema` SYSTEM_PROMPT (the single source of
 * truth for what each field means); keep them in sync.
 */

export type LabelTabKey = 'label' | 'browse' | 'upload' | 'stats';

export const LABEL_TABS: { key: LabelTabKey; label: string }[] = [
  { key: 'label', label: 'Label' },
  { key: 'browse', label: 'Browse' },
  { key: 'upload', label: 'Upload' },
  { key: 'stats', label: 'Stats' },
];

/** The ten schema fields, in form order, with a one-line labeler definition.
 *  Keys must match FormValues (minus `notes`, which isn't a schema field). */
export const FIELD_GUIDE: { field: string; label: string; help: string }[] = [
  {
    field: 'document_type',
    label: 'Document type',
    help: "What the document is — invoice, estimate, statement of account, engagement letter, or other. Use 'unreadable' only when the text is too garbled to classify.",
  },
  {
    field: 'supplier_name_on_invoice',
    label: 'Supplier name',
    help: 'The issuing business, exactly as printed — the vendor who sent the invoice, not the customer.',
  },
  {
    field: 'invoice_date',
    label: 'Invoice date',
    help: 'The issue date, never the due date. Format YYYY-MM-DD.',
  },
  {
    field: 'invoice_total',
    label: 'Invoice total',
    help: 'The final amount payable (Total / Amount Due / Balance Due). Digits and a decimal point only — strip currency symbols and thousands separators.',
  },
  {
    field: 'supplier_bn',
    label: 'Supplier BN',
    help: 'The supplier\'s CRA business number as written, e.g. "123456789RT0001", or a bare 9-digit root near GST/HST wording. Foreign suppliers usually have none — leave it blank.',
  },
  {
    field: 'tax_indication',
    label: 'Tax indication',
    help: 'How tax is documented — an itemized dollar amount, a "tax included" statement, just a rate, or none. Records how tax is shown, not which tax it is. Use "none" for exempt / zero-rated / no tax line.',
  },
  {
    field: 'tax_shown',
    label: 'Tax shown',
    help: 'The total sales tax printed on the document — digits and a decimal point only. Blank when no tax amount is shown.',
  },
  {
    field: 'invoice_number',
    label: 'Invoice number',
    help: 'The invoice or document number exactly as printed.',
  },
  {
    field: 'recipient_name',
    label: 'Recipient name',
    help: 'The bill-to customer as printed — the business being invoiced. Do not swap this with the supplier.',
  },
  {
    field: 'payment_terms',
    label: 'Payment terms',
    help: 'Payment terms as printed, e.g. "Net 30" or "Due on receipt".',
  },
  {
    field: 'supplier_jurisdiction',
    label: 'Supplier jurisdiction',
    help: 'Where the supplier is based, read from its printed address: Canada, US, or Other. US/Other suppliers usually show no BN or GST/HST — but only mark a field "not on document" when the invoice itself doesn\'t show it, never because of the jurisdiction.',
  },
];

/** The conventions every labeler follows, regardless of field. */
export const LABELING_CONVENTIONS: string[] = [
  'Read only what is printed. Never guess and never derive — do not compute tax from a rate; enter only amounts the document actually shows.',
  'Supplier is who issued the invoice; recipient is who is being billed. Keep them straight.',
  'The parser pre-fills each card — correct what it got wrong, then Submit & next. Every correction sharpens the extractor.',
  "If a field genuinely isn't on the document, leave it blank. If the whole document is unreadable, use Skip — don't guess a label.",
];
